class ProductReviewsIOFit extends HTMLElement {
    constructor() {
        super();
        this.sku = this.getAttribute('sku');
        this.maxRetries = 4;
        this.widgetObserver = null;
        this.resizeObserver = null;
        this.hasRenderedIndicator = false;
    }

    connectedCallback() {
        const cachedLeft = this.getCachedIndicatorPosition();

        if (cachedLeft) {
            this.renderSizingIndicator(cachedLeft);
        }

        if (!this.syncFromWidget()) {
            this.fetchReviews();
        }

        this.observeWidget();
        this.observeWidgetSize();
    }

    disconnectedCallback() {
        if (this.widgetObserver) {
            this.widgetObserver.disconnect();
            this.widgetObserver = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    returnEmptyReview() {
        // 5 stars empty with a disabled class and a (0)
        this.innerHTML = `<div class="product-reviews-io__stars">
            <div class="product-reviews-io__stars-container">
                <div class="product-reviews-io__stars-empty disabled">
                    ${this.getStarsSVG()}
                </div>
                <div class="product-reviews-io__stars-filled disabled" style="width: 100%;">
                    ${this.getStarsSVG()}
                </div>
            </div>
            <span class="product-reviews-io__stars-count">(0)</span>${this.link ? `<a href="#ReviewsWidget"></a>` : ''}
        </div>`;
    }

    retryFetch(attempt) {
        if (attempt >= this.maxRetries) {
            if (!this.hasRenderedIndicator) {
                this.innerHTML = '';
            }
            return;
        }

        window.setTimeout(() => {
            this.fetchReviews(attempt + 1);
        }, 400 * (attempt + 1));
    }

    fetchReviews(attempt = 0) {
        if (!this.sku) {
            return;
        }

        fetch(`https://api.reviews.io/reviews?store=teveo.com&sku=${encodeURIComponent(this.sku)}&type=product_review&include_subrating_breakdown=true&per_page=1`)
            .then(response => response.json())
            .then(data => {
                if (!data.stats || !data.stats.attributes) {
                    this.retryFetch(attempt);
                    return;
                }
                // a number from 1 to 5
                const fitAttribute = data.stats.attributes.find(attribute => attribute.id === 9410)

                if (!fitAttribute) {
                    this.retryFetch(attempt);
                    return;
                }
                const fitScore = fitAttribute.average_rating;

                if (!fitScore) {
                    this.retryFetch(attempt);
                    return;
                }

                const floatFitScore = parseFloat(fitScore);

                if (!floatFitScore) {
                    this.retryFetch(attempt);
                    return;
                }
                const left = Math.max(0, Math.min(100, ((floatFitScore - 1) / 4) * 100));
                this.renderSizingIndicator(`${left}%`);
            })
            .catch(() => {
                this.retryFetch(attempt);
            });

    };

    observeWidget() {
        const widget = document.getElementById('ReviewsWidget');

        if (!widget) {
            return;
        }

        this.widgetObserver = new MutationObserver(() => {
            this.syncFromWidget();
        });

        this.widgetObserver.observe(widget, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });
    }

    observeWidgetSize() {
        const widget = document.getElementById('ReviewsWidget');

        if (!widget || typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.syncFromWidget();
        });

        this.resizeObserver.observe(widget);
    }

    syncFromWidget() {
        const sizingIndicator = Array.from(document.querySelectorAll('#ReviewsWidget .R-SliderIndicator')).find((indicator) => {
            const heading = indicator.querySelector('.R-TextHeading--xxxs')?.textContent?.trim()?.toLowerCase() || '';
            return ['sizing', 'size', 'größe', 'groesse', 'fit', 'passform'].includes(heading);
        });
        const innerWidth = sizingIndicator?.querySelector('.R-SliderIndicator__inner')?.getBoundingClientRect().width || 0;
        const buttonLeft = sizingIndicator?.querySelector('.R-SliderIndicator__button')?.style?.left;

        if (!buttonLeft || innerWidth <= 0) {
            return false;
        }

        this.renderSizingIndicator(buttonLeft);
        return true;
    }

    getCachedIndicatorPosition() {
        if (!this.sku || typeof window === 'undefined') {
            return '';
        }

        const fitCache = window.__teveoReviewsIoFitCache || {};
        return fitCache[this.sku] || '';
    }

    setCachedIndicatorPosition(leftValue) {
        if (!this.sku || typeof window === 'undefined') {
            return;
        }

        window.__teveoReviewsIoFitCache = window.__teveoReviewsIoFitCache || {};
        window.__teveoReviewsIoFitCache[this.sku] = leftValue;
    }

    renderSizingIndicator(leftValue) {
        const smallLabel = (typeof sizeIndicatorStrings !== 'undefined' && sizeIndicatorStrings.small) ? sizeIndicatorStrings.small : 'Small';
        const trueLabel = (typeof sizeIndicatorStrings !== 'undefined' && sizeIndicatorStrings.true) ? sizeIndicatorStrings.true : 'True to size';
        const bigLabel = (typeof sizeIndicatorStrings !== 'undefined' && sizeIndicatorStrings.big) ? sizeIndicatorStrings.big : 'Big';

        this.hasRenderedIndicator = true;
        this.setCachedIndicatorPosition(leftValue);

        this.innerHTML = `<div class="product-sizing-slider__wrapper">
          <div class="product-sizing-slider__background">
            <div class="product-sizing-slider__track">
              <div class="product-sizing-slider__thumb" style="left: ${leftValue};"></div>
            </div>
          </div>
          <div class="product-sizing-slider__labels">
            <div class="product-sizing-slider__label">
              <span>${smallLabel}</span>
            </div>
            <div class="product-sizing-slider__label">
              <span>${trueLabel}</span>
            </div>
            <div class="product-sizing-slider__label">
              <span>${bigLabel}</span>
            </div>
          </div>
     
        </div>`;
    }
}


customElements.define('product-reviews-io-fit', ProductReviewsIOFit);

class ProductReviewsIOStars extends HTMLElement {
    constructor() {
        super();
        this.sku = this.getAttribute('sku');
        this.link = this.getAttribute('link');
    }

    connectedCallback() {
        this.fetchReviews();
    }

    returnEmptyReview() {
        // 5 stars had to be empty of stars to keep being a placeholder, but was asked to be removed
        this.remove();
        

        // this.innerHTML = `<div class="product-reviews-io__stars no-reviews">
        //     <div class="product-reviews-io__stars-container">
        //         <div class="product-reviews-io__stars-empty ">
        //             ${this.getStarsSVG()}
        //         </div>
      
        //     </div>
        //     <span class="product-reviews-io__stars-count">(0)</span>${this.link ? `<a href="#ReviewsWidget"></a>` : ''}
        // </div>`;
    }

    fetchReviews() {
        if (!this.sku) {
            this.returnEmptyReview();
            return;
        }

        fetch(`https://api.reviews.io/reviews?store=teveo.com&sku=${encodeURIComponent(this.sku)}&type=product_review&include_subrating_breakdown=false&per_page=1`)
            .then(response => response.json())
            .then(data => {
                if (!data.review_count || data.review_count === 0) {
                    this.returnEmptyReview();
                    return;
                }

                const rating = parseFloat(data.average_rating) || 0;
                const reviewCount = data.review_count || 0;
                const fillPercentage = (rating / 5) * 100;

                this.innerHTML = `<div class="product-reviews-io__stars">
                    <div class="product-reviews-io__stars-container">
                        <div class="product-reviews-io__stars-empty">
                            ${this.getStarsSVG()}
                        </div>
                        <div class="product-reviews-io__stars-filled" style="width: ${fillPercentage}%;">
                            ${this.getStarsSVG()}
                        </div>
                    </div>
                    
                        <span class="product-reviews-io__stars-count">(${reviewCount})</span>${this.link ? `<a href="${this.link}"></a>` : ''}
                </div>`;
            });
    };

    getStarsSVG() {
        const starSVG = `<svg class="product-reviews-io__star" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>`;
        return starSVG.repeat(5);
    }
}

customElements.define('product-reviews-io-stars', ProductReviewsIOStars);

class ProductReviewsIOSection extends HTMLElement {
    constructor() {
        super();
        this.sku = this.getAttribute('sku');
        this.widgetObserver = null;
        this.resizeObserver = null;
        this.syncScheduled = false;
        this.handleResize = this.queueSyncHeaderLayout.bind(this);
    }

    connectedCallback() {
        this.setSectionPendingState();
        this.fetchReviewState();
        this.syncHeaderLayout();
        this.observeWidget();
        this.observeWidgetSize();
        window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
        if (this.widgetObserver) {
            this.widgetObserver.disconnect();
            this.widgetObserver = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        window.removeEventListener('resize', this.handleResize);
    }

    fetchReviewState() {
        if (!this.sku) {
            this.setSectionVisibility(false);
            return;
        }

        fetch(`https://api.reviews.io/reviews?store=teveo.com&sku=${encodeURIComponent(this.sku)}&type=product_review&include_subrating_breakdown=true&per_page=1`)
            .then(response => response.json())
            .then(data => {
                const hasReviews = Number(data?.review_count) > 0;

                this.setSectionVisibility(hasReviews);

                if (hasReviews) {
                    this.queueSyncHeaderLayout();
                }
            })
            .catch(() => {
                this.setSectionVisibility(true);
                this.queueSyncHeaderLayout();
            });
    }

    setSectionPendingState() {
        this.hidden = false;
        this.style.setProperty('visibility', 'hidden');
    }

    setSectionVisibility(isVisible) {
        this.style.removeProperty('visibility');
        this.hidden = !isVisible;
    }

    observeWidget() {
        const widget = document.getElementById('ReviewsWidget');

        if (!widget) {
            return;
        }

        this.widgetObserver = new MutationObserver(() => {
            this.queueSyncHeaderLayout();
        });

        this.widgetObserver.observe(widget, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });
    }

    observeWidgetSize() {
        const widget = document.getElementById('ReviewsWidget');

        if (!widget || typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.queueSyncHeaderLayout();
        });

        this.resizeObserver.observe(widget);
    }

    queueSyncHeaderLayout() {
        if (this.syncScheduled) {
            return;
        }

        this.syncScheduled = true;

        window.requestAnimationFrame(() => {
            this.syncScheduled = false;
            this.syncHeaderLayout();
        });
    }

    syncHeaderLayout() {
        const widget = document.getElementById('ReviewsWidget');
        const headerInner = widget?.querySelector('.ElementsWidget__header .header__inner');
        const leftGroup = headerInner?.querySelector('.header__group--left');
        const centerGroup = headerInner?.querySelector('.header__group--center');
        const rightGroup = headerInner?.querySelector('.header__group--right');
        const firstReviewItem = widget?.querySelector('.R-ContentList__item');
        const metaColumn = firstReviewItem?.querySelector('.item__meta');

        if (!headerInner || !leftGroup || !centerGroup) {
            return;
        }

        if (!window.matchMedia('(min-width: 750px)').matches) {
            this.resetHeaderLayout(headerInner, leftGroup, centerGroup);
            return;
        }

        if (!metaColumn) {
            return;
        }

        const leftColumnWidth = metaColumn.getBoundingClientRect().width;

        if (!leftColumnWidth) {
            return;
        }

        const columnGap = getComputedStyle(widget).getPropertyValue('--reviewsio-columns-gap').trim() || '3.2rem';
        const metricsPaddingStart = getComputedStyle(widget).getPropertyValue('--reviewsio-metrics-padding-start').trim() || '3rem';

        widget.style.setProperty('--reviewsio-left-column-width', `${leftColumnWidth}px`);
        headerInner.style.setProperty('display', 'grid', 'important');
        headerInner.style.setProperty('grid-template-columns', `${leftColumnWidth}px minmax(0, 1fr)`, 'important');
        headerInner.style.setProperty('gap', columnGap, 'important');
        headerInner.style.setProperty('align-items', 'start', 'important');
        headerInner.style.setProperty('justify-content', 'initial', 'important');

        leftGroup.style.setProperty('grid-column', '1', 'important');
        leftGroup.style.setProperty('width', '100%', 'important');
        leftGroup.style.setProperty('max-width', 'none', 'important');
        leftGroup.style.setProperty('justify-self', 'stretch', 'important');

        centerGroup.style.setProperty('grid-column', '2', 'important');
        centerGroup.style.setProperty('width', '100%', 'important');
        centerGroup.style.setProperty('max-width', 'none', 'important');
        centerGroup.style.setProperty('min-width', '0', 'important');
        centerGroup.style.setProperty('padding-inline-start', metricsPaddingStart, 'important');
        centerGroup.style.setProperty('justify-self', 'stretch', 'important');

        if (rightGroup) {
            rightGroup.style.setProperty('display', 'none', 'important');
        }
    }

    resetHeaderLayout(headerInner, leftGroup, centerGroup) {
        headerInner.style.removeProperty('display');
        headerInner.style.removeProperty('grid-template-columns');
        headerInner.style.removeProperty('gap');
        headerInner.style.removeProperty('align-items');
        headerInner.style.removeProperty('justify-content');

        leftGroup.style.removeProperty('grid-column');
        leftGroup.style.removeProperty('justify-self');

        centerGroup.style.removeProperty('grid-column');
        centerGroup.style.removeProperty('min-width');
        centerGroup.style.removeProperty('padding-inline-start');
        centerGroup.style.removeProperty('justify-self');
    }
}

customElements.define('product-reviews-io-section', ProductReviewsIOSection);

class ProductReviewsIOSummary extends HTMLElement {
    constructor() {
        super();
        this.sku = this.getAttribute('sku');
        this.eyebrow = this.getAttribute('eyebrow') || '';
        this.title = this.readAttribute('title', 'Kundenstimmen');
        this.subtitle = this.getAttribute('subtitle') || '';
        this.link = this.getAttribute('link') || '';
        this.ctaLabel = this.getAttribute('cta-label') || '';
        this.widgetObserver = null;
        this.resizeObserver = null;
        this.syncScheduled = false;
        this.ready = false;
        this.hasReviews = null;
    }

    connectedCallback() {
        this.setSectionVisibility(false);
        this.renderShell();
        this.fetchReviewState();
        this.syncFromWidget();
        this.observeWidget();
        this.observeWidgetSize();
    }

    disconnectedCallback() {
        if (this.widgetObserver) {
            this.widgetObserver.disconnect();
            this.widgetObserver = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    renderShell() {
        const safeEyebrow = this.escapeHTML(this.eyebrow);
        const safeTitle = this.escapeHTML(this.title);
        const safeSubtitle = this.escapeHTML(this.subtitle);

        this.innerHTML = `<div class="product-reviews-summary">
            ${safeEyebrow || safeSubtitle ? `<div class="product-reviews-summary__heading-group">
                ${safeEyebrow ? `<span class="product-reviews-summary__eyebrow text-body--subline">${safeEyebrow}</span>` : ''}
                ${safeSubtitle ? `<h2 class="product-reviews-summary__heading recommended-products__heading">${safeSubtitle}</h2>` : ''}
            </div>` : ''}
            <div class="product-reviews-summary__overview">
                <div class="product-reviews-summary__score">
                    ${safeTitle ? `<p class="product-reviews-summary__score-label">${safeTitle}</p>` : ''}
                    <div class="product-reviews-summary__score-content"></div>
                </div>
                <div class="product-reviews-summary__metrics"></div>
            </div>
            ${this.link && this.ctaLabel ? `<a class="product-reviews-summary__cta button button--secondary" href="${this.escapeHTML(this.link)}">${this.escapeHTML(this.ctaLabel)}</a>` : ''}
        </div>`;
    }

    fetchReviewState() {
        if (!this.sku) {
            this.hasReviews = false;
            this.hideSection();
            return;
        }

        fetch(`https://api.reviews.io/reviews?store=teveo.com&sku=${encodeURIComponent(this.sku)}&type=product_review&include_subrating_breakdown=true&per_page=1`)
            .then(response => response.json())
            .then(data => {
                this.hasReviews = Number(data?.review_count) > 0;

                if (!this.hasReviews) {
                    this.hideSection();
                    return;
                }

                this.queueSyncFromWidget();
            })
            .catch(() => {
                this.hasReviews = null;
            });
    }

    observeWidget() {
        const widget = document.getElementById('ReviewsWidget');

        if (!widget) {
            this.hideSection();
            return;
        }

        this.widgetObserver = new MutationObserver(() => {
            this.queueSyncFromWidget();
        });

        this.widgetObserver.observe(widget, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });
    }

    observeWidgetSize() {
        const widget = document.getElementById('ReviewsWidget');

        if (!widget || typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.queueSyncFromWidget();
        });

        this.resizeObserver.observe(widget);
    }

    queueSyncFromWidget() {
        if (this.syncScheduled) {
            return;
        }

        this.syncScheduled = true;

        window.requestAnimationFrame(() => {
            this.syncScheduled = false;
            this.syncFromWidget();
        });
    }

    syncFromWidget() {
        if (this.hasReviews === false) {
            this.hideSection();
            return;
        }

        const widget = document.getElementById('ReviewsWidget');
        const header = widget?.querySelector('.ElementsWidget__header');
        const headerInner = header?.querySelector('.header__inner');
        const leftGroup = headerInner?.querySelector('.header__group--left');
        const metricsGroup = headerInner?.querySelector('.header__group--center .R-SliderIndicator-group');
        const headerWidth = headerInner?.getBoundingClientRect().width || 0;
        const metricsWidth = metricsGroup?.getBoundingClientRect().width || 0;
        const metrics = Array.from(metricsGroup?.querySelectorAll('.R-SliderIndicator') || []);
        const hasHydratedMetrics = headerWidth > 0 && metricsWidth > 0 && metrics.length > 0 && metrics.every(metric => {
            const inner = metric.querySelector('.R-SliderIndicator__inner');
            const button = metric.querySelector('.R-SliderIndicator__button');
            const innerWidth = inner?.getBoundingClientRect().width || 0;

            return inner && button && innerWidth > 0 && button.style.left && window.getComputedStyle(button).display !== 'none';
        });
        const scoreTarget = this.querySelector('.product-reviews-summary__score-content');
        const metricsTarget = this.querySelector('.product-reviews-summary__metrics');

        if (!leftGroup || !metricsGroup || !hasHydratedMetrics || !scoreTarget || !metricsTarget) {
            if (!this.ready) {
                this.hideSection();
            }
            return;
        }

        scoreTarget.replaceChildren(leftGroup.cloneNode(true));
        metricsTarget.replaceChildren(metricsGroup.cloneNode(true));

        this.ready = true;
        this.setSectionVisibility(true);
    }

    hideSection() {
        this.setSectionVisibility(false);
    }

    setSectionVisibility(isVisible) {
        const section = this.closest('.product-reviews-summary-section');

        this.hidden = !isVisible;

        if (section) {
            section.hidden = !isVisible;
        }
    }

    escapeHTML(value) {
        const replacements = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        };

        return String(value).replace(/[&<>"']/g, character => replacements[character]);
    }

    readAttribute(name, fallback) {
        const value = this.getAttribute(name);
        return value === null ? fallback : value;
    }
}

customElements.define('product-reviews-io-summary', ProductReviewsIOSummary);
